import React, { Fragment, useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useParams } from 'react-router-dom';
import { assetsChaptersBulkUpdateCreate } from '@goldcast/api/content';
import { ChapterStartAndEndTime, IndividualChapterPropInterface } from './ChapterItem';
import RenameChapter from './RenameChapter';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../timeUtils';
import { menuItems } from '@/Pages/Sessions/AssetsTable/AssetsTableUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { loadChapters } from '@/context/TranscriptContext/TranscriptContextUtils';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

type MenuItemsLabelType = string;

interface RowMenuItemProps {
  chapter: IndividualChapterPropInterface;
  chapterStartAndEndTime: ChapterStartAndEndTime;
  isDragging: boolean;
}

export default function ChapterActionMenu({ chapter, chapterStartAndEndTime, isDragging }: RowMenuItemProps) {
  const chapterMenuItem = menuItems['CHAPTER'];
  const { broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isRenamingChapter, setIsRenamingChapter] = useState(false);
  const { clipData } = useClipsContext();
  const { onCreateClip: createClip } = useCreateClip({ clip: clipData });
  const { logger } = useAppContext();
  const [isDeletingChapter, setIsDeletingChapter] = useState(false);
  const chapterId = chapter.id;
  const transcriptStore = useTranscriptContext();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const {
    isOpen: isRemoveChapterConfirmationOpen,
    openDialog: openRemoveChapterConfirmationDialog,
    closeDialog: closeRemoveChapterConfirmationDialog
  } = useDialog();
  const chapterContainerRef = useRef<HTMLDivElement>(null);

  const onCreateClip = useCallback(() => {
    const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);
    let createChapterObj = {
      start_time: chapterStartAndEndTime.start_time,
      end_time: chapterStartAndEndTime.end_time,
      start_segment_id: '',
      end_segment_id: ''
    };
    if (isSplitsEnabled) {
      const startSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.start_time, clipData);
      const endSegment = getSegmentAtCurrentFrame(chapterStartAndEndTime.end_time, clipData);
      if (startSegment && endSegment) {
        createChapterObj = {
          start_time: getSegmentTimeFromPlayerTime(chapterStartAndEndTime.start_time, clipData, startSegment?.id),
          end_time: getSegmentTimeFromPlayerTime(chapterStartAndEndTime.end_time, clipData, endSegment?.id),
          start_segment_id: startSegment?.id || '',
          end_segment_id: endSegment?.id || ''
        };
      }
    }

    createClip(
      createChapterObj.start_time,
      createChapterObj.end_time,
      createChapterObj.start_segment_id,
      createChapterObj.end_segment_id
    )
      .then(() => {
        showSuccessToast('Clip created successfully');
      })
      .catch(err => {
        showErrorToast('Error while creating clip');
        logger.error('Error while creating clip', err);
      });
  }, [clipData, chapterStartAndEndTime.start_time, chapterStartAndEndTime.end_time, createClip, logger]);

  const onRemoveChapter = useCallback(() => {
    if (!chapterId || !broadcastId || !chapters) {
      return;
    }
    setIsDeletingChapter(true);
    const updatedChapters = chapters.map(c => ({ ...c }));
    const chapterIndex = updatedChapters.findIndex(c => c.id === chapterId);
    const toRemoveChapter = updatedChapters[chapterIndex];

    if (chapterIndex !== chapters.length - 1) {
      updatedChapters[chapterIndex + 1].start_time = toRemoveChapter.start_time;
    }

    assetsChaptersBulkUpdateCreate({
      body: updatedChapters.filter(c => c.id !== chapterId),
      queryParams: {
        content_id: broadcastId
      }
    })
      .then(async () => {
        const updatedChapters = await loadChapters(broadcastId);
        EventBus.dispatch(CustomEvents.UpdateChapters, {
          chapterData: updatedChapters?.chapters,
          chapterGenerationStatus: updatedChapters?.chapter_generation_status,
          chaptersLastUpdatedAt: updatedChapters?.latest_update_at
        });
        showSuccessToast('Chapter removed successfully');
      })
      .catch(err => {
        showErrorToast('Error while removing chapter');
        logger.error('Error while removing chapter', err);
      })
      .finally(() => {
        setIsDeletingChapter(false);
        closeRemoveChapterConfirmationDialog();
      });
  }, [chapterId, broadcastId, chapters, logger, closeRemoveChapterConfirmationDialog]);

  const onAction = useCallback(
    (actionType: MenuItemsLabelType) => {
      switch (actionType) {
        case 'Rename Chapter':
          setIsRenamingChapter(true);
          break;
        case 'Create Clip':
          onCreateClip();
          break;
        case 'Remove Chapter':
          openRemoveChapterConfirmationDialog();
          break;
      }
    },
    [onCreateClip, openRemoveChapterConfirmationDialog]
  );

  useEffect(() => {
    const buttonElement = buttonRef.current;

    if (buttonElement) {
      const handleMouseEnter = () => {
        setIsTooltipOpen(true);
      };
      const handleMouseLeave = () => {
        setIsTooltipOpen(false);
      };

      buttonElement.addEventListener('mouseenter', handleMouseEnter);
      buttonElement.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        if (buttonElement) {
          buttonElement.removeEventListener('mouseenter', handleMouseEnter);
          buttonElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }
  }, []);

  //  !! TODO: @HiteshChoudhary0098 Add tooltip here after floating ui is implemented
  return (
    <>
      <div className="relative flex w-full" ref={chapterContainerRef}>
        <Menu as="div" className="relative flex w-full items-start justify-start">
          <Menu.Button
            ref={buttonRef}
            className="group/chapter-container relative w-full truncate rounded-full px-[9px] py-[7px] text-start text-2xs font-normal text-slate-600"
            data-testid="chapter-actions-menu-button"
            onClick={e => {
              if (!chapterContainerRef.current) {
                return;
              }
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                clientX: e.clientX,
                clientY: e.clientY
              });
              chapterContainerRef.current.dispatchEvent(clickEvent);
            }}
          >
            {chapter.title}
          </Menu.Button>
          <Transition
            as={Fragment}
            show={isTooltipOpen && !isDragging}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <div
              className="fixed -bottom-1 z-20 h-[21px] w-fit -translate-y-full rounded-sm border border-zinc-900 bg-zinc-900 px-1.5 py-0.5 text-2xs leading-[15px] text-white"
              data-testid="chapter-actions-menu-tooltip"
              style={
                buttonRef.current
                  ? {
                      top: `${buttonRef.current.getBoundingClientRect().top - 4}px`,
                      left: `${buttonRef.current.getBoundingClientRect().left}px`
                    }
                  : {}
              }
            >
              {chapter.title}
            </div>
          </Transition>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <div
              className="fixed inset-0 z-[70]"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onMouseMove={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Menu.Items
                style={{
                  position: 'fixed',
                  top: `${buttonRef.current?.getBoundingClientRect().top || 0 - 10}px`,
                  left: `${Math.min(buttonRef.current?.getBoundingClientRect().left || 0, window.innerWidth - 200)}px`
                }}
                className={
                  'z-50 w-48 -translate-x-1 -translate-y-full rounded-lg border bg-white p-1 text-sm text-black shadow-lg'
                }
              >
                {chapterMenuItem?.map(item => (
                  <Menu.Item as="div" key={item.label}>
                    <button
                      className={'flex w-full items-center space-x-3 rounded-md px-3 py-2 hover:bg-slate-100'}
                      onClick={() => onAction(item.label as MenuItemsLabelType)}
                    >
                      <item.icon size={18} stroke={1.5} />
                      <span>{item.label}</span>
                    </button>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </div>
          </Transition>
        </Menu>
      </div>
      <ConfirmationDialog
        isOpen={isRemoveChapterConfirmationOpen}
        onClose={closeRemoveChapterConfirmationDialog}
        onConfirm={onRemoveChapter}
        confirmLoading={isDeletingChapter}
        title="Remove Chapter"
        content="Are you sure you want to remove this chapter break in the video? You will not be able to revert this change."
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
      {isRenamingChapter && <RenameChapter chapter={chapter} onClose={() => setIsRenamingChapter(false)} />}
    </>
  );
}
